import { Box, Button, Card, Divider, IconButton, Icons, Stack, Typography } from '@healthinal/ui';
import { useState } from 'react';
import { LabOrderDto, LabOrderResultDto, LabOrderResultDtoType } from '../../../api/generated/model';
import { CardRow } from '../../../components/CardRow.tsx';
import { SubCard } from '../../../components/SubCard.tsx';
import { TypographyLink } from '../../../components/TypographyLink.tsx';
import { formatDateWithOptionalTime } from '../../../utils/date.ts';
import {
  getLabOrderResultDtoTypeName,
  getLabOrderResultInterpretationName,
  getLabOrderResultName,
  getLabOrderTypeName,
  hasLabOrderResults,
} from '../../../utils/labOrder.ts';
import { getValidUrlForLink } from '../../../utils/url.ts';
import { FileLink } from '../FileLink.tsx';
import { EditLabOrderResultDialog } from './EditLabOrderResultDialog.tsx';

export function LabOrderCard({ order, onEditDialogClose }: { order: LabOrderDto; onEditDialogClose?: () => void }) {
  const [isEditLabOrderDialogOpen, setIsEditLabOrderDialogOpen] = useState(false);

  return (
    <>
      <Card>
        <Stack direction={'row'} alignItems={'start'} gap={3}>
          <Stack flex={2} gap={5}>
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'start'}>
              <Stack direction={'row'} gap={2}>
                <SubCard>
                  <Icons.Bloodtype />
                </SubCard>
                <Stack justifyContent={'space-between'}>
                  <Typography level={'h4'}>{getLabOrderTypeName(order.type)}</Typography>
                  <Typography level={'body-sm'}>
                    {formatDateWithOptionalTime(order.samplingDate, order.samplingTime)}
                  </Typography>
                </Stack>
              </Stack>
              {!hasLabOrderResults(order) && (
                <Button onClick={() => setIsEditLabOrderDialogOpen(true)}>Resultat eintragen</Button>
              )}
            </Stack>
            <Stack gap={1}>
              <CardRow title={'Resultat'} content={getLabOrderResultInterpretationName(order.interpretation)} />
              <Divider />
              <CardRow
                title={'Durchgeführt von'}
                content={
                  [order.samplingContact?.lastName, order.samplingContact?.firstName].filter(Boolean).join(' ') || '-'
                }
              />
              <Divider />
              <CardRow title={'Erfasst von'} content={`${order.createdBy?.lastName} ${order.createdBy?.firstName}`} />
              {order.createdBy?.id != order.updatedBy?.id && (
                <CardRow
                  title={'Aktualisiert von'}
                  content={`${order.updatedBy?.lastName} ${order.updatedBy?.firstName}`}
                />
              )}
              <Divider />
              <CardRow title={'Werte'}>
                <Box flex={3}>
                  {order.results.map((result) => (
                    <LabOrderResult key={result.id} resultToDisplay={result} allResults={order.results} />
                  ))}
                </Box>
              </CardRow>
              {order.fileDetails && (
                <>
                  <Divider />
                  <CardRow title={'Datei'}>
                    <FileLink flex={3} fileDetails={order.fileDetails} />
                  </CardRow>
                </>
              )}
            </Stack>
          </Stack>
          <Stack flex={1}>
            <Typography level={'title-lg'}>Bemerkungen</Typography>
            <Typography level={'body-sm'} sx={{ whiteSpace: 'pre-line' }}>
              {order.notes}
            </Typography>
          </Stack>
          <IconButton color={'primary'} onClick={() => setIsEditLabOrderDialogOpen(true)} title={'Test bearbeiten'}>
            <Icons.Edit />
          </IconButton>
        </Stack>
      </Card>
      <EditLabOrderResultDialog
        open={isEditLabOrderDialogOpen}
        onClose={() => {
          setIsEditLabOrderDialogOpen(false);
          if (onEditDialogClose) {
            onEditDialogClose();
          }
        }}
        order={order}
      />
    </>
  );
}

function LabOrderResult({
  resultToDisplay,
  allResults,
}: {
  resultToDisplay: LabOrderResultDto;
  allResults: LabOrderResultDto[];
}) {
  if (resultToDisplay.type.startsWith('SPUTUM_')) {
    if (resultToDisplay.type.startsWith('SPUTUM_MATERIAL_')) {
      const materialName = resultToDisplay.type.replace('SPUTUM_MATERIAL_', '');
      const methods = allResults.filter((result) => result.type.startsWith(`SPUTUM_METHOD_${materialName}`));
      return (
        <Stack pb={1}>
          <Typography
            level={'title-sm'}
            endDecorator={
              resultToDisplay.result && <Typography level={'body-sm'}>{resultToDisplay.result}</Typography>
            }>
            {getLabOrderResultDtoTypeName(resultToDisplay.type)}
          </Typography>

          {methods.map((method) => (
            <Typography key={method.id} level={'body-sm'}>
              {getLabOrderResultDtoTypeName(method.type)}
              {method.name}: {getLabOrderResultName(method.type, method.result)}
            </Typography>
          ))}
        </Stack>
      );
    }
  } else {
    return (
      <Typography level={'body-sm'} endDecorator={<Typography level={'title-sm'}>{resultToDisplay.unit}</Typography>}>
        {getLabOrderResultDtoTypeName(resultToDisplay.type)}
        {resultToDisplay.name && ` ${resultToDisplay.name}`}: {getLabOrderResultDisplay(resultToDisplay)}
      </Typography>
    );
  }
}

function getLabOrderResultDisplay(resultToDisplay: LabOrderResultDto) {
  if (resultToDisplay.type === LabOrderResultDtoType.XRAY_FILE_LINK) {
    const url = getValidUrlForLink(resultToDisplay.result);
    return (
      <TypographyLink marginLeft={0.5} href={url} level={'body-sm'} target={'_blank'}>
        {resultToDisplay.result}
      </TypographyLink>
    );
  }
  return (
    <Typography marginLeft={0.5} level={'title-sm'}>
      {getLabOrderResultName(resultToDisplay.type, resultToDisplay.result)}
    </Typography>
  );
}
